import { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import { useNodes } from '../hooks/useNodes';

// Fix for default marker icons in webpack/vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom cluster icon
function createClusterIcon(cluster) {
  const count = cluster.getChildCount();
  let size = 'small';
  let dimensions = 40;

  if (count >= 100) {
    size = 'large';
    dimensions = 60;
  } else if (count >= 10) {
    size = 'medium';
    dimensions = 50;
  }

  return L.divIcon({
    html: `<div class="custom-cluster-icon ${size}" style="width: ${dimensions}px; height: ${dimensions}px; line-height: ${dimensions}px;">${count}</div>`,
    className: '',
    iconSize: [dimensions, dimensions]
  });
}

// Cluster layer component
function ClusterLayer({ nodes, onNodeClick, onClusterClick }) {
  const map = useMap();
  const clusterGroupRef = useRef(null);

  useEffect(() => {
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    const markers = L.markerClusterGroup({
      iconCreateFunction: createClusterIcon,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 18,
      maxClusterRadius: 50
    });

    nodes.forEach(node => {
      const marker = L.marker([node.coordinate.lat, node.coordinate.lng], {
        nodeData: node
      });

      marker.on('click', () => {
        onNodeClick(node);
      });

      markers.addLayer(marker);
    });

    markers.on('clusterclick', (e) => {
      const cluster = e.layer;
      const childMarkers = cluster.getAllChildMarkers();
      const clusterNodes = childMarkers.map(m => m.options.nodeData);

      // If zoomed in enough or few nodes, show popup instead of zooming
      if (map.getZoom() >= 15 || clusterNodes.length <= 10) {
        onClusterClick(clusterNodes, e.latlng);
      }
    });

    map.addLayer(markers);
    clusterGroupRef.current = markers;

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
  }, [map, nodes, onNodeClick, onClusterClick]);

  return null;
}

// Node detail popup content
function NodeDetailPopup({ node, onClose }) {
  if (!node) return null;

  return (
    <div className="node-popup">
      <h3>{node.name}</h3>
      <div className="info-row">
        <span className="info-label">Location:</span>
        <span className="info-value">
          {node.city && node.country
            ? `${node.city}, ${node.country}`
            : node.city || node.country || 'Unknown'}
        </span>
      </div>
      <div className="info-row">
        <span className="info-label">Coordinates:</span>
        <span className="info-value">
          {node.coordinate.lat.toFixed(6)}, {node.coordinate.lng.toFixed(6)}
        </span>
      </div>
      {node.comment && (
        <div className="info-row">
          <span className="info-label">Comment:</span>
          <span className="info-value">{node.comment}</span>
        </div>
      )}
      {node.metadata && Object.keys(node.metadata).length > 0 && (
        <div className="metadata">
          <strong>Metadata:</strong>
          <pre>{JSON.stringify(node.metadata, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// Cluster popup with node list
function ClusterPopup({ nodes, onNodeSelect }) {
  if (!nodes || nodes.length === 0) return null;

  return (
    <div className="cluster-popup">
      <h3>{nodes.length} Nodes</h3>
      <ul>
        {nodes.map(node => (
          <li key={node.id} onClick={() => onNodeSelect(node)}>
            <strong>{node.name}</strong>
            {(node.city || node.country) && (
              <span style={{ marginLeft: '8px', color: '#7f8c8d' }}>
                {node.city && node.country
                  ? `${node.city}, ${node.country}`
                  : node.city || node.country}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Popup manager component
function PopupManager({ selectedNode, clusterNodes, clusterPosition, onNodeSelect, onClose, onClusterClose, onNodeClose }) {
  const map = useMap();
  const clusterPopupRef = useRef(null);
  const nodePopupRef = useRef(null);
  // Use separate close handlers if provided, fallback to onClose
  const handleClusterClose = onClusterClose || onClose;
  const handleNodeClose = onNodeClose || onClose;

  // Manage cluster popup
  useEffect(() => {
    if (clusterNodes && clusterNodes.length > 0 && clusterPosition) {
      // Close existing cluster popup if any
      if (clusterPopupRef.current) {
        map.closePopup(clusterPopupRef.current);
        clusterPopupRef.current = null;
      }

      const content = document.createElement('div');
      content.className = 'cluster-popup';
      content.innerHTML = `
        <h3>${clusterNodes.length} Nodes</h3>
        <ul>
          ${clusterNodes.map(node => `
            <li data-node-id="${node.id}">
              <strong>${node.name}</strong>
              ${(node.city || node.country) ? `
                <span style="margin-left: 8px; color: #7f8c8d;">
                  ${node.city && node.country
                    ? `${node.city}, ${node.country}`
                    : node.city || node.country}
                </span>
              ` : ''}
            </li>
          `).join('')}
        </ul>
      `;

      // Add click handlers to list items
      content.querySelectorAll('li').forEach(li => {
        li.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          const nodeId = li.getAttribute('data-node-id');
          const node = clusterNodes.find(n => n.id === nodeId);
          if (node) {
            onNodeSelect(node);
          }
        });
      });

      const popup = L.popup({
        maxWidth: 350,
        maxHeight: 400
      })
        .setLatLng(clusterPosition)
        .setContent(content)
        .openOn(map);

      popup.on('remove', handleClusterClose);
      clusterPopupRef.current = popup;
    } else if (clusterPopupRef.current) {
      // If clusterNodes is cleared, close the cluster popup
      map.closePopup(clusterPopupRef.current);
      clusterPopupRef.current = null;
    }

    return () => {
      if (clusterPopupRef.current) {
        map.closePopup(clusterPopupRef.current);
      }
    };
  }, [map, clusterNodes, clusterPosition, onNodeSelect, handleClusterClose]);

  // Manage node popup
  useEffect(() => {
    if (selectedNode) {
      // Close existing node popup if any
      if (nodePopupRef.current) {
        map.closePopup(nodePopupRef.current);
        nodePopupRef.current = null;
      }

      const content = document.createElement('div');
      content.innerHTML = `
        <div class="node-popup">
          <h3>${selectedNode.name}</h3>
          <div class="info-row">
            <span class="info-label">Location:</span>
            <span class="info-value">
              ${selectedNode.city && selectedNode.country
                ? `${selectedNode.city}, ${selectedNode.country}`
                : selectedNode.city || selectedNode.country || 'Unknown'}
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">Coordinates:</span>
            <span class="info-value">
              ${selectedNode.coordinate.lat.toFixed(6)}, ${selectedNode.coordinate.lng.toFixed(6)}
            </span>
          </div>
          ${selectedNode.comment ? `
            <div class="info-row">
              <span class="info-label">Comment:</span>
              <span class="info-value">${selectedNode.comment}</span>
            </div>
          ` : ''}
          ${selectedNode.metadata && Object.keys(selectedNode.metadata).length > 0 ? `
            <div class="metadata">
              <strong>Metadata:</strong>
              <pre>${JSON.stringify(selectedNode.metadata, null, 2)}</pre>
            </div>
          ` : ''}
        </div>
      `;

      const popup = L.popup({
        maxWidth: 350,
        maxHeight: 400
      })
        .setLatLng([selectedNode.coordinate.lat, selectedNode.coordinate.lng])
        .setContent(content)
        .openOn(map);

      popup.on('remove', handleNodeClose);
      nodePopupRef.current = popup;
    } else if (nodePopupRef.current) {
      // If selectedNode is cleared, close the node popup
      map.closePopup(nodePopupRef.current);
      nodePopupRef.current = null;
    }

    return () => {
      if (nodePopupRef.current) {
        map.closePopup(nodePopupRef.current);
      }
    };
  }, [map, selectedNode, handleNodeClose]);

  return null;
}

export default function MapPage() {
  const { nodes, loading, error } = useNodes();
  const [selectedNode, setSelectedNode] = useState(null);
  const [clusterNodes, setClusterNodes] = useState(null);
  const [clusterPosition, setClusterPosition] = useState(null);

  const handleNodeClick = useCallback((node) => {
    setClusterNodes(null);
    setClusterPosition(null);
    setSelectedNode(node);
  }, []);

  const handleClusterClick = useCallback((nodes, position) => {
    setSelectedNode(null);
    setClusterNodes(nodes);
    setClusterPosition(position);
  }, []);

  const handleNodeSelectFromCluster = useCallback((node) => {
    // Keep cluster popup open, just set selected node
    setSelectedNode(node);
  }, []);

  const handlePopupClose = useCallback(() => {
    setSelectedNode(null);
    setClusterNodes(null);
    setClusterPosition(null);
  }, []);

  const handleClusterPopupClose = useCallback(() => {
    setClusterNodes(null);
    setClusterPosition(null);
  }, []);

  const handleNodePopupClose = useCallback(() => {
    setSelectedNode(null);
  }, []);

  if (loading) {
    return (
      <div className="map-page">
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-page">
        <div className="message error">Error loading nodes: {error}</div>
      </div>
    );
  }

  return (
    <div className="map-page">
      <MapContainer
        center={[20, 0]}
        zoom={2}
        className="map-container"
        worldCopyJump={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClusterLayer
          nodes={nodes}
          onNodeClick={handleNodeClick}
          onClusterClick={handleClusterClick}
        />
        <PopupManager
          selectedNode={selectedNode}
          clusterNodes={clusterNodes}
          clusterPosition={clusterPosition}
          onNodeSelect={handleNodeSelectFromCluster}
          onClose={handlePopupClose}
          onClusterClose={handleClusterPopupClose}
          onNodeClose={handleNodePopupClose}
        />
      </MapContainer>
      {nodes.length === 0 && (
        <div className="map-instruction">
          No nodes yet. Go to Admin to add some nodes.
        </div>
      )}
    </div>
  );
}
